import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Navigation } from '@angular/router';
import { Store } from '@ngrx/store';
import dateFormat from 'date-fns/format';
import { forEach as _forEach, has as _has } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions } from '@amfam/billing/one-time-pay/feature';
import {
  fromPaymentHistoryActions,
  paymentHistoryQuery
} from '@amfam/billing/payment-history/data-access';
import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentMethod } from '@amfam/billing/payment-method/util';
import { PaymentAccountSelectors } from '@amfam/billing/paymentaccount/data-access';
import {
  ScheduledPaymentActions,
  ScheduledPaymentSelectors
} from '@amfam/billing/schedulepayment/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillingAnalytics,
  MonthRange,
  SchedulePaymentPayloadModel
} from '@amfam/shared/models';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { AlertModalConfig, DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-new-payment-history',
  templateUrl: './new-payment-history.component.html',
  styleUrls: ['./new-payment-history.component.scss'],
  standalone: false
})
export class NewPaymentHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() navigation: Navigation;
  @ViewChild('paymentHistoryContainer') paymentHistoryContainer: ElementRef;
  public paymentHistoryForm;

  public paymentHistoryLoading$: Observable<boolean>;
  public dataLoading$: Observable<boolean>;
  public hasError$: Observable<boolean>;

  public monthRanges: { range: MonthRange; text: string }[] = [
    { range: { startMonth: 0, endMonth: -3 }, text: 'Future payments' },
    { range: { startMonth: 3, endMonth: 0 }, text: 'Past 0 to 3 months' },
    { range: { startMonth: 6, endMonth: 3 }, text: 'Past 4 to 6 months' },
    { range: { startMonth: 9, endMonth: 6 }, text: 'Past 7 to 9 months' },
    { range: { startMonth: 12, endMonth: 9 }, text: 'Past 10 to 12 months' },
    { range: { startMonth: 15, endMonth: 12 }, text: 'Past 13 to 15 months' },
    { range: { startMonth: 18, endMonth: 15 }, text: 'Past 16 to 18 months' },
    { range: { startMonth: 21, endMonth: 18 }, text: 'Past 19 to 21 months' },
    { range: { startMonth: 24, endMonth: 21 }, text: 'Past 22 to 24 months' }
  ];

  paymentHistoryCopy = this.copyService.getCopy('billing.statementHistory');
  editBtnCopy = this.copyService.getCopy('billing.paymentHistory.editBtnCopy');
  deleteBtnCopy = this.copyService.getCopy('billing.paymentHistory.deleteBtnCopy');
  riskDescriptionOption = [];
  historyListLimit = 6;
  paymentHistorydisplayLimit = 0;
  paymentHistoryListForView = [];
  CompletePaymentsList = [];
  totalPossiblePayments;
  billAccountToRiskMap = [];
  scrollPaymentIntoView = false;
  paymentToBeDeleted;

  deletePendingPaymentModalConfig: AlertModalConfig = {
    headerText: 'Are you sure you want to delete?',
    bodyText:
      'You’ll need to make sure you set up another payment to stay current on your account.',
    modalId: 'deletePendingPaymentModal',
    ctaConfig: {
      primaryButtonName: 'Delete',
      primaryButtonDestructive: true,
      tertiaryButtonName: 'Cancel',
      fullWidthButtons: true
    }
  };
  actionInFlight: boolean;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private currencyUSDPipe: CurrencyUSDPipe = new CurrencyUSDPipe(),
    private analyticsFacade: AnalyticsFacade,
    private copyService: CopyService,
    private modalService: DsModalService,
    private utilService: UtilService,
    private spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    // This component uses the localized spinner instead of the application level spinner

    const futurePayments = this.navigation?.extras?.state?.futurePayments;
    const billAccNumber = this.navigation?.extras?.state?.billAccNumber;

    this.setupPaymentHistoryForm(futurePayments, billAccNumber);

    this.paymentHistorydisplayLimit = this.historyListLimit;

    // Data is served from the payment history store.
    this.paymentHistoryLoading$ = this.store.select(paymentHistoryQuery.selectLoading);
    this.hasError$ = this.store.select(paymentHistoryQuery.selectError);

    this.dataLoading$ = combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsAnyLoading),
      this.store.select(PaymentMethodSelectors.selectLoading),
      this.store.select(PaymentAccountSelectors.selectPaymentAccountsAnyLoading)
    ]).pipe(
      map(
        ([
          billAccountsLoading,
          scheduledPaymentsLoading,
          paymentMethodsLoading,
          paymentAccountsLoading
        ]) =>
          billAccountsLoading ||
          scheduledPaymentsLoading ||
          paymentMethodsLoading ||
          paymentAccountsLoading
      )
    );

    // Subscribe to data from the payment history store and payment methods
    combineLatest([
      this.store.select(paymentHistoryQuery.selectMonthRange),
      this.store.select(paymentHistoryQuery.selectPayments),
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(PaymentMethodSelectors.selectPaymentMethods)
    ])
      .pipe(
        map(([monthRange, payments, billAccounts, paymentMethod]) => ({
          monthRange,
          payments,
          billAccounts,
          paymentMethod
        })),
        filter(state => Object.keys(state.paymentMethod).length > 0), // Filter out empty paymentMethod objects
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        // Following is to mix the billaccount data with the payments data and format it for display
        let list = {};
        this.CompletePaymentsList = [];
        for (const payment of state.payments) {
          for (const paymentBillAccount of payment.billAccounts) {
            list = this.setHistoryList(
              payment,
              paymentBillAccount,
              state.billAccounts,
              state.paymentMethod
            );
            this.CompletePaymentsList.push(list);
          }
        }

        // Filter and set the paymentHistoryListForView based on billaccount dropdown
        this.filterAndSetPaymentHistoryList();

        // Set the dropdown options for the billaccount dropdown
        this.billAccountToRiskMap = [];
        this.riskDescriptionOption = ['All'];
        _forEach(state.billAccounts, (billAccount: BillAccount) => {
          if (_has(billAccount, 'billAccountNumber')) {
            this.setBillAccountOption(billAccount);
          }
        });

        // Set the bill account Dropdown if navigation has billAccNumber
        if (billAccNumber) {
          this.scrollPaymentIntoView = true;
          const dropDownValueForBillAcc = this.billAccountToRiskMap.find(
            billAccToRisk => billAccToRisk.billAccountNumber === billAccNumber
          );
          if (dropDownValueForBillAcc) {
            this.paymentHistoryForm.patchValue({
              billAccount: dropDownValueForBillAcc.riskDesc.join()
            });
          }
        }
      });
  }

  ngAfterViewInit() {
    // Scroll into view the payment history form
    if (this.scrollPaymentIntoView) {
      this.paymentHistoryContainer.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  loadMore() {
    this.paymentHistorydisplayLimit += this.historyListLimit;
    this.filterAndSetPaymentHistoryList();
  }

  onEditPayment(paymentId) {
    this.store.dispatch(
      OneTimePayActions.routeToReviewForEdit({
        paymentId
      })
    );
  }

  onDeletePayment(payment) {
    this.paymentToBeDeleted = payment;
    this.modalService.open('deletePendingPaymentModal');
  }

  removePendingPayment() {
    this.modalService.closeAll();
    const inputDate = dateFormat(this.paymentToBeDeleted.paymentDate, 'YYYY-MM-DD');
    // generate correlation id
    const correlationId = this.utilService.generateId();
    const paymentId = this.paymentToBeDeleted.paymentId;
    const paymentInfoObj: SchedulePaymentPayloadModel = {
      payment: {
        receivedDate: inputDate,
        totalPaymentAmount: Number(this.paymentToBeDeleted.totalPaymentAmount).toFixed(2),
        paymentAccount: this.paymentToBeDeleted.paymentAccountObj,
        billAccounts: this.paymentToBeDeleted.billAccounts
      },
      paymentId: paymentId,
      correlationId: correlationId
    };
    this.actionInFlight = true;
    this.spinner.start({
      blockActions: true
    });

    this.store.dispatch(ScheduledPaymentActions.scheduledPaymentsDelete(paymentInfoObj));
    this.store.dispatch(OneTimePayActions.storePaymentId({ paymentId }));
  }

  cancel() {
    this.modalService.close('deletePendingPaymentModal');
  }

  private requestHistory(rangeIndex: number) {
    this.store.dispatch(
      fromPaymentHistoryActions.GetPayments({ monthRange: this.monthRanges[rangeIndex].range })
    );
  }

  private setupPaymentHistoryForm(futurePayments, billAccNumber) {
    // The payments table will show future payments for the specific billaccount when view payments is clicked on the bill details screen
    if (futurePayments && billAccNumber) {
      this.paymentHistoryForm = this.formBuilder.group({
        monthRange: [0, Validators.required],
        billAccount: ['All', Validators.required] // appropriate dropdown value will be set in combineLatest when the options are available
      });

      this.requestHistory(0); // So that the future payements are requested from the API
    } else {
      this.paymentHistoryForm = this.formBuilder.group({
        monthRange: [1, Validators.required],
        billAccount: ['All', Validators.required]
      });
      this.requestHistory(1); // So that 0 to 3 months payements are requested from the API
    }

    // Two different form change listeners here, one for month range and another for billaccount

    // when month range changes call the api to get the data, the data for view will be set in the onInit combineLatest
    this.paymentHistoryForm
      .get('monthRange')
      ?.valueChanges.pipe(takeUntil(this.stop$))
      .subscribe(value => {
        // Send analytics on user selection from the drop down.
        const dateRange = BillingAnalytics.paymentHistoryDateRange(this.monthRanges[value].text);
        this.analyticsFacade.trackPage(dateRange);
        this.paymentHistorydisplayLimit = this.historyListLimit;
        this.requestHistory(value);
      });

    // when billaccount changes filter the paymentHistoryListForView
    this.paymentHistoryForm
      .get('billAccount')
      ?.valueChanges.pipe(takeUntil(this.stop$))
      .subscribe(() => {
        // Filter and set the paymentHistoryListForView based on billaccount dropdown
        this.filterAndSetPaymentHistoryList();
      });
  }

  private setBillAccountOption(billAccount: BillAccount) {
    if (billAccount.policyList?.length && billAccount.policyList[0].riskDescriptionList) {
      const riskDesc = billAccount.policyList[0].riskDescriptionList;
      if (!this.riskDescriptionOption.includes(riskDesc)) {
        this.riskDescriptionOption.push(riskDesc);
        this.billAccountToRiskMap.push({
          billAccountNumber: billAccount.billAccountNumber,
          riskDesc: riskDesc
        });
      }
    } else if (billAccount.policyTypeDisplayName) {
      const riskDesc = billAccount.policyTypeDisplayName;
      if (!this.riskDescriptionOption.includes(riskDesc)) {
        this.riskDescriptionOption.push(riskDesc);
        this.billAccountToRiskMap.push({ billAccount: riskDesc });
      }
    }
  }

  private getCardDetailsByNickName(nickName, paymentMethods: PaymentMethod[]) {
    for (const [, value] of Object.entries(paymentMethods)) {
      if (value.nickName === nickName) {
        if (value.creditCard) {
          return `(...${value.creditCard.cardNumber.slice(-4)})`;
        } else if (value.achWithdrawal) {
          return `(...${value.achWithdrawal.accountNumber.slice(-4)})`;
        }
      }
    }
    return '';
  }

  private setHistoryList(
    payment,
    paymentBillAccount,
    billAccounts,
    paymentMethods: PaymentMethod[]
  ) {
    let paymentAccount = '';
    if (payment?.paymentAccount?.nickName) {
      paymentAccount =
        payment.paymentAccount.nickName +
        this.getCardDetailsByNickName(payment.paymentAccount.nickName, paymentMethods);
    } else {
      paymentAccount = payment.paymentMethod;
    }
    const list = {
      paymentStatus: this.getPaymentStatus(payment.paymentStatus),
      paymentAccount: paymentAccount,
      risk: this.getRiskDescription(payment, billAccounts),
      paymentAmount: this.currencyUSDPipe.transform(paymentBillAccount.billAcctPaymentAmount),
      paymentDate: payment.receivedDate,
      paymentId: payment.paymentId,
      totalPaymentAmount: payment.totalPaymentAmount,
      paymentAccountObj: payment.paymentAccount,
      billAccounts: payment.billAccounts
    };
    return list;
  }

  private filterAndSetPaymentHistoryList() {
    const billAcountChange = this.paymentHistoryForm.controls.billAccount.value?.split(',');
    let tmpList;
    if (!billAcountChange.includes('All')) {
      tmpList = this.CompletePaymentsList.filter(
        payment => JSON.stringify(payment.risk) === JSON.stringify(billAcountChange)
      );
    } else {
      tmpList = this.CompletePaymentsList;
    }
    this.totalPossiblePayments = tmpList.length;
    this.paymentHistoryListForView = tmpList.slice(0, this.paymentHistorydisplayLimit);
  }

  private getPaymentStatus(paymentStatus) {
    let displayStatus = paymentStatus;
    switch (paymentStatus) {
      case 'SUCCESSFUL':
        displayStatus = 'Paid';
        break;
      case 'UNSUCCESSFUL':
        displayStatus = 'Failed';
        break;
      case 'DELETED':
        displayStatus = 'Deleted';
        break;
      case 'SCHEDULED':
        displayStatus = 'Scheduled payment';
        break;
      case 'AUTHORIZED':
        displayStatus = 'Processing';
        break;
    }
    return displayStatus;
  }

  private getRiskDescription(payment, billAccounts) {
    let risk;
    const paymentAccountBill = payment.billAccounts[0].billAccountNumber;
    for (const account of billAccounts) {
      if (account.billAccountNumber === paymentAccountBill) {
        if (account.policyList?.length && account.policyList[0].riskDescriptionList) {
          risk = account.policyList[0].riskDescriptionList;
        } else if (account.policyTypeDisplayName) {
          risk = account.policyTypeDisplayName;
        }
      }
    }
    return risk;
  }
}
